/**
 * CSRF-protected API client utility
 */

/**
 * Make a CSRF-protected API request
 * @param {string} url - The API endpoint URL
 * @param {Object} options - Fetch options
 * @param {string} csrfToken - The CSRF token
 * @returns {Promise<Response>} - The fetch response
 */
export async function csrfFetch(url, options = {}, csrfToken) {
  const {
    method = 'GET',
    headers = {},
    body,
    ...otherOptions
  } = options;

  // Prepare headers
  const requestHeaders = {
    'Content-Type': 'application/json',
    ...headers,
  };

  // Add CSRF token for state-changing requests
  if (csrfToken && ['POST', 'PUT', 'DELETE', 'PATCH'].includes(method.toUpperCase())) {
    requestHeaders['X-CSRF-Token'] = csrfToken;
  }

  // Prepare request options
  const requestOptions = {
    method,
    headers: requestHeaders,
    credentials: 'include', // Include cookies for session
    ...otherOptions,
  };

  // Add body for non-GET requests
  if (body && method.toUpperCase() !== 'GET') {
    requestOptions.body = typeof body === 'string' ? body : JSON.stringify(body);
  }

  try {
    const response = await fetch(url, requestOptions);
    
    // Handle CSRF token errors
    if (response.status === 403) {
      const errorData = await response.json().catch(() => ({}));
      if (errorData.error?.includes('CSRF') || errorData.error?.includes('csrf')) {
        console.error('CSRF: Token validation failed');
        throw new Error('CSRF token validation failed. Please refresh the page and try again.');
      }
    }

    return response;
  } catch (error) {
    console.error('CSRF: API request failed:', error);
    throw error;
  }
}

/**
 * Make a CSRF-protected POST request
 * @param {string} url - The API endpoint URL
 * @param {Object} data - The request body
 * @param {string} csrfToken - The CSRF token
 * @param {Object} options - Additional fetch options
 * @returns {Promise<Response>} - The fetch response
 */
export async function csrfPost(url, data, csrfToken, options = {}) {
  return csrfFetch(url, {
    method: 'POST',
    body: data,
    ...options,
  }, csrfToken);
}

/**
 * Make a CSRF-protected PUT request
 * @param {string} url - The API endpoint URL
 * @param {Object} data - The request body
 * @param {string} csrfToken - The CSRF token
 * @param {Object} options - Additional fetch options
 * @returns {Promise<Response>} - The fetch response
 */
export async function csrfPut(url, data, csrfToken, options = {}) {
  return csrfFetch(url, {
    method: 'PUT',
    body: data,
    ...options,
  }, csrfToken);
}

/**
 * Make a CSRF-protected DELETE request
 * @param {string} url - The API endpoint URL
 * @param {string} csrfToken - The CSRF token
 * @param {Object} options - Additional fetch options
 * @returns {Promise<Response>} - The fetch response
 */
export async function csrfDelete(url, csrfToken, options = {}) {
  return csrfFetch(url, {
    method: 'DELETE',
    ...options,
  }, csrfToken);
}

/**
 * Make a CSRF-protected PATCH request
 * @param {string} url - The API endpoint URL
 * @param {Object} data - The request body
 * @param {string} csrfToken - The CSRF token
 * @param {Object} options - Additional fetch options
 * @returns {Promise<Response>} - The fetch response
 */
export async function csrfPatch(url, data, csrfToken, options = {}) {
  return csrfFetch(url, {
    method: 'PATCH',
    body: data,
    ...options,
  }, csrfToken);
}

/**
 * Make a GET request (no CSRF token needed)
 * @param {string} url - The API endpoint URL
 * @param {Object} options - Additional fetch options
 * @returns {Promise<Response>} - The fetch response
 */
export async function csrfGet(url, options = {}) {
  return csrfFetch(url, {
    method: 'GET',
    ...options,
  });
} 